/**
 * Custom PDP — product media gallery, recommendations fetch + slider.
 */

/**
 * @param {HTMLElement} gallery
 */
const pauseGalleryMedia = (gallery) => {
  gallery.querySelectorAll('video').forEach((video) => {
    video.pause();
  });

  gallery.querySelectorAll('iframe').forEach((iframe) => {
    const src = iframe.getAttribute('src');
    if (!src) return;
    iframe.setAttribute('src', src);
  });
};

/** @type {((callback: () => void) => void) | null} */
let runWhenSwiperReady = null;

/**
 * Waits for Swiper CDN (theme footer, after section scripts).
 * @param {() => void} callback
 */
const whenSwiperReady = (callback) => {
  if (typeof window.Swiper === 'function') {
    callback();
    return;
  }

  if (runWhenSwiperReady) {
    runWhenSwiperReady(callback);
    return;
  }

  /** @type {(() => void)[]} */
  const queue = [];
  let done = false;

  const flush = () => {
    if (done || typeof window.Swiper !== 'function') {
      return;
    }
    done = true;
    window.clearInterval(timer);
    const pending = queue.splice(0, queue.length);
    for (const fn of pending) {
      fn();
    }
  };

  runWhenSwiperReady = (fn) => {
    if (typeof window.Swiper === 'function') {
      fn();
      return;
    }
    queue.push(fn);
    flush();
  };

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    flush();
    if (done || attempts >= 200) {
      window.clearInterval(timer);
    }
  }, 100);

  window.addEventListener('load', flush, { once: true });
  queue.push(callback);
};

/**
 * @param {HTMLElement} gallery
 * @param {import('swiper').Swiper} mainSwiper
 */
const playActiveGalleryVideo = (gallery, mainSwiper) => {
  pauseGalleryMedia(gallery);
  const slide = mainSwiper.slides[mainSwiper.activeIndex];
  const video = slide?.querySelector('video');
  if (video) {
    video.play().catch(() => {});
  }
};

/**
 * @param {HTMLElement} gallery
 * @param {number | string} mediaId
 */
const syncPdpGalleryToMediaId = (gallery, mediaId) => {
  if (!(gallery instanceof HTMLElement) || mediaId == null) return;

  const mainEl = gallery.querySelector('[data-pdp-main-swiper]');
  if (!(mainEl instanceof HTMLElement)) return;

  const mediaIdStr = String(mediaId);
  const slides = mainEl.querySelectorAll('.swiper-slide[data-media-id]');
  const slideIndex = Array.from(slides).findIndex(
    (slide) => slide.dataset.mediaId === mediaIdStr
  );

  if (slideIndex < 0) return;

  if (mainEl.swiper) {
    mainEl.swiper.slideTo(slideIndex);
    return;
  }

  slides.forEach((slide, index) => {
    slide.hidden = index !== slideIndex;
  });
};

/**
 * @typedef {Object} PdpVariantFormatted
 * @property {string} price
 * @property {string} [compareAtPrice]
 * @property {string} [unitPrice]
 */

/**
 * @param {HTMLElement} productRoot
 * @returns {{ variants: object[], formatted: Record<string, PdpVariantFormatted> } | null}
 */
const getPdpVariantPriceData = (productRoot) => {
  const dataEl =
    productRoot.querySelector('variant-picker [type="application/json"]') ||
    productRoot.querySelector('[data-pdp-variant-prices]');

  if (!dataEl?.textContent) return null;

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    return null;
  }
};

/**
 * @param {HTMLElement} priceEl
 * @param {object} variant
 * @param {Record<string, PdpVariantFormatted>} formatted
 */
const updatePdpPriceDisplay = (priceEl, variant, formatted) => {
  if (!priceEl || !variant) return;

  const variantFormatted = formatted[variant.id] || formatted[String(variant.id)];
  if (!variantFormatted?.price) return;

  const priceCurrentEl = priceEl.querySelector('.price__current');
  const priceWasEl = priceEl.querySelector('.price__was');
  const priceWasAmountEl = priceEl.querySelector('.price__was-amount');
  const priceOffEl = priceEl.querySelector('.price__off');
  const onSale = variant.compare_at_price > variant.price;
  const formattedCompareAt = variantFormatted.compareAtPrice || '';

  if (priceCurrentEl) {
    priceCurrentEl.innerHTML = variantFormatted.price;
  }

  if (priceWasAmountEl) {
    priceWasAmountEl.innerHTML = onSale ? formattedCompareAt : '';
  }

  if (priceWasEl) {
    priceWasEl.hidden = !onSale;
  }

  if (priceOffEl) {
    if (onSale && variant.compare_at_price > 0) {
      const discountPercent = Math.round(
        ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
      );
      priceOffEl.textContent = `(${discountPercent}% Off)`;
      priceOffEl.hidden = false;
    } else {
      priceOffEl.textContent = '';
      priceOffEl.hidden = true;
    }
  }

  priceEl.classList.toggle('price--on-sale', onSale);
  priceEl.classList.toggle('price--sold-out', !variant.available);
};

/**
 * @param {HTMLElement} productRoot
 * @param {object} variant
 */
const syncPdpPriceFromVariant = (productRoot, variant) => {
  if (!variant) return;

  const data = getPdpVariantPriceData(productRoot);
  if (!data) return;

  const priceEl = productRoot.querySelector('.product-custom-detail__price .price');
  if (!priceEl) return;

  updatePdpPriceDisplay(priceEl, variant, data.formatted);
};

/**
 * @param {HTMLElement} productRoot
 * @param {string | number} variantId
 */
const syncPdpPriceFromVariantId = (productRoot, variantId) => {
  const data = getPdpVariantPriceData(productRoot);
  if (!data) return;

  const id = Number(variantId);
  if (!Number.isFinite(id)) return;

  const variant = data.variants.find((entry) => entry.id === id);
  syncPdpPriceFromVariant(productRoot, variant);
};

/**
 * @param {HTMLElement} root
 */
const applyHideVariantOptionMatch = (root) => {
  const productRoot = root.matches?.('.js-product.product-custom-detail')
    ? root
    : root.querySelector?.('.js-product.product-custom-detail');

  if (!(productRoot instanceof HTMLElement)) return;

  const matchText = productRoot.dataset.hideVariantOptionMatch?.trim();
  if (!matchText) return;

  const matchLower = matchText.toLowerCase();
  const seoRoot = productRoot.querySelector('#seo-variants');
  if (!seoRoot) return;

  for (const group of seoRoot.querySelectorAll('.seo-variants__list, fieldset')) {
    const label = group.querySelector('.seo-variants__label, legend, p');
    const labelText = label?.textContent?.trim().toLowerCase() || '';
    if (labelText.includes(matchLower)) {
      group.style.display = 'none';
    }
  }

  for (const item of seoRoot.querySelectorAll('.seo-variants__item')) {
    const text = item.textContent?.trim().toLowerCase() || '';
    if (text.includes(matchLower)) {
      item.style.display = 'none';
    }
  }
};

/**
 * @param {HTMLElement} root
 */
const bindPdpVariantGallerySync = (root) => {
  const productRoot = root.matches?.('.js-product.product-custom-detail')
    ? root
    : root.querySelector?.('.js-product.product-custom-detail');

  if (!(productRoot instanceof HTMLElement) || productRoot.dataset.pdpVariantGalleryBound === 'true') {
    return;
  }

  productRoot.dataset.pdpVariantGalleryBound = 'true';

  productRoot.addEventListener('on:variant:change', (event) => {
    syncPdpPriceFromVariant(productRoot, event.detail?.variant);

    const gallery = productRoot.querySelector('[data-product-media-gallery]');
    const featuredMediaId = event.detail?.variant?.featured_media?.id;
    if (!gallery || !featuredMediaId) return;
    syncPdpGalleryToMediaId(gallery, featuredMediaId);
  });

  for (const form of productRoot.querySelectorAll('.js-product-form')) {
    const idInput = form.querySelector('[name="id"]');
    if (!idInput) continue;

    idInput.addEventListener('change', () => {
      syncPdpPriceFromVariantId(productRoot, idInput.value);
    });
  }

  const seoVariants = productRoot.querySelector('#seo-variants');
  if (seoVariants) {
    seoVariants.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.seo-variants__button, .seo-variants__item, .swatch-border')) return;

      window.requestAnimationFrame(() => {
        const idInput = productRoot.querySelector('.js-product-form [name="id"]');
        if (idInput) syncPdpPriceFromVariantId(productRoot, idInput.value);
      });
    });
  }
};

/**
 * @param {HTMLElement} gallery
 */
const initProductMediaGallery = (gallery) => {
  if (!(gallery instanceof HTMLElement)) return;

  const mainEl = gallery.querySelector('[data-pdp-main-swiper]');
  const thumbEl = gallery.querySelector('[data-pdp-thumb-swiper]');

  if (mainEl?.swiper) {
    mainEl.swiper.destroy(true, true);
  }

  if (thumbEl?.swiper) {
    thumbEl.swiper.destroy(true, true);
  }

  const slideEls = mainEl ? mainEl.querySelectorAll('.swiper-slide') : [];
  const productForm = gallery.closest('.js-product');

  bindPdpVariantGallerySync(productForm || gallery);

  if (!mainEl || slideEls.length < 2) {
    gallery.dataset.galleryInit = 'true';
    return;
  }

  /** @type {import('swiper').Swiper | null} */
  let thumbSwiper = null;

  const thumbPrevBtn = gallery.querySelector('[data-pdp-thumb-prev]');
  const thumbNextBtn = gallery.querySelector('[data-pdp-thumb-next]');

  if (thumbEl instanceof HTMLElement) {
    /** @type {import('swiper').SwiperOptions} */
    const thumbConfig = {
      spaceBetween: 10,
      slidesPerView: 4,
      freeMode: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      breakpoints: {
        0: { slidesPerView: 4 },
        769: { slidesPerView: 4 },
        1200: { slidesPerView: 5 },
      },
    };

    if (thumbPrevBtn instanceof HTMLButtonElement && thumbNextBtn instanceof HTMLButtonElement) {
      thumbConfig.navigation = {
        prevEl: thumbPrevBtn,
        nextEl: thumbNextBtn,
      };
    }

    thumbSwiper = new window.Swiper(thumbEl, thumbConfig);
  }

  const paginationEl = mainEl.querySelector('.product-custom-detail__main-pagination');

  /** @type {any} */
  const mainConfig = {
    spaceBetween: 0,
    speed: 300,
    allowTouchMove: true,
    watchOverflow: true,
    grabCursor: true,
    on: {
      slideChange() {
        playActiveGalleryVideo(gallery, this);
      },
    },
  };

  if (thumbSwiper) {
    mainConfig.thumbs = { swiper: thumbSwiper };
  }

  if (paginationEl instanceof HTMLElement) {
    mainConfig.pagination = { el: paginationEl, clickable: true };
  }

  const mainSwiper = new window.Swiper(mainEl, mainConfig);

  const variantPicker = productForm?.querySelector('variant-picker[loaded]');
  const initialVariant = variantPicker?.variant;
  if (initialVariant?.featured_media?.id) {
    syncPdpGalleryToMediaId(gallery, initialVariant.featured_media.id);
  }

  gallery.dataset.galleryInit = 'true';
};

/** @type {HTMLElement | null} */
let pdpCouponLiveRegion = null;

/**
 * @param {string} text
 * @returns {Promise<boolean>}
 */
const copyTextToClipboard = async (text) => {
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Use legacy fallback below.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
};

/**
 * @param {string} message
 */
const announcePdpCouponCopy = (message) => {
  if (!pdpCouponLiveRegion) {
    pdpCouponLiveRegion = document.createElement('div');
    pdpCouponLiveRegion.setAttribute('role', 'status');
    pdpCouponLiveRegion.setAttribute('aria-live', 'polite');
    pdpCouponLiveRegion.className = 'visually-hidden';
    document.body.appendChild(pdpCouponLiveRegion);
  }

  pdpCouponLiveRegion.textContent = message;
};

/**
 * @param {ParentNode} root
 */
const initPdpCouponCopy = (root = document) => {
  for (const box of root.querySelectorAll('.coupon-box[data-pdp-coupon-copy]')) {
    if (!(box instanceof HTMLElement)) continue;
    if (box.dataset.pdpCouponCopyBound === 'true') continue;

    const code = box.dataset.couponCode?.trim();
    if (!code) continue;

    box.dataset.pdpCouponCopyBound = 'true';

    box.addEventListener('click', async (event) => {
      event.preventDefault();

      const copied = await copyTextToClipboard(code);
      if (!copied) return;

      announcePdpCouponCopy(`Coupon code ${code} copied`);
      box.setAttribute('data-coupon-copied', 'true');
      window.setTimeout(() => {
        box.removeAttribute('data-coupon-copied');
      }, 2000);
    });
  }
};

const bootProductMediaGalleries = () => {
  document.querySelectorAll('.js-product.product-custom-detail').forEach((root) => {
    bindPdpVariantGallerySync(root);
    applyHideVariantOptionMatch(root);
  });
  initPdpCouponCopy();

  whenSwiperReady(() => {
    document.querySelectorAll('[data-product-media-gallery]').forEach(initProductMediaGallery);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductMediaGalleries);
} else {
  bootProductMediaGalleries();
}

window.addEventListener('load', () => {
  document.querySelectorAll('.js-product.product-custom-detail').forEach(applyHideVariantOptionMatch);
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  bindPdpVariantGallerySync(target);
  applyHideVariantOptionMatch(target);
  initPdpCouponCopy(target);

  whenSwiperReady(() => {
    target.querySelectorAll('[data-product-media-gallery]').forEach(initProductMediaGallery);
  });
});

(() => {
  if (window.__kairausPdpRecommendationsBooted) return;
  window.__kairausPdpRecommendationsBooted = true;

  const dispatchGridInit = (root) => {
    document.dispatchEvent(
      new CustomEvent('kairaus:product-grid:init', {
        bubbles: true,
        detail: { root },
      })
    );
  };

  const initSuggestionsSlider = (root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.pdpSliderInit === 'true') return;

    const track = root.querySelector('.suggested-products-wrap');
    const nextBtn = root.querySelector('.browse-sug-arrows .next');
    const prevBtn = root.querySelector('.browse-sug-arrows .prev');
    if (!track || !nextBtn || !prevBtn) return;

    const baseCards = Array.from(track.children).filter((node) => node.nodeType === Node.ELEMENT_NODE);
    if (!baseCards.length) return;

    let position = 0;
    let isAnimating = false;

    const getVisibleCards = () => {
      const w = window.innerWidth;
      if (w <= 575) return 1;
      if (w <= 767) return 2;
      if (w <= 1023) return 3;
      if (w <= 1439) return 4;
      return 5;
    };

    let visibleCards = getVisibleCards();

    track.innerHTML += track.innerHTML;
    const cards = Array.from(track.children);

    const getGap = () => (window.innerWidth <= 1440 ? 18 : 24);

    const getCardWidth = () => cards[0].getBoundingClientRect().width + getGap();

    const moveSlider = () => {
      track.style.transition = 'transform .3s ease';
      track.style.transform = `translateX(-${position * getCardWidth()}px)`;
      isAnimating = true;
    };

    nextBtn.addEventListener('click', () => {
      if (isAnimating) return;
      position += 1;
      moveSlider();
    });

    prevBtn.addEventListener('click', () => {
      if (isAnimating) return;
      position -= 1;
      moveSlider();
    });

    track.addEventListener('transitionend', () => {
      const total = baseCards.length;
      if (position >= total) {
        track.style.transition = 'none';
        position = 0;
        track.style.transform = 'translateX(0px)';
      }
      if (position < 0) {
        track.style.transition = 'none';
        position = total - 1;
        track.style.transform = `translateX(-${position * getCardWidth()}px)`;
      }
      isAnimating = false;
    });

    window.addEventListener('resize', () => {
      const newVisible = getVisibleCards();
      if (newVisible !== visibleCards) {
        visibleCards = newVisible;
        track.style.transition = 'none';
        track.style.transform = `translateX(-${position * getCardWidth()}px)`;
      }
    });

    root.dataset.pdpSliderInit = 'true';
  };

  const initRecommendationsSection = (section) => {
    if (!section.querySelector('.suggested-products-wrap')?.children?.length) return;
    section.removeAttribute('data-pdp-slider-init');
    initSuggestionsSlider(section);

    // Defer so layout + Swiper (defer CDN) are ready after AJAX inject
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dispatchGridInit(section);
      });
    });
  };

  const buildPdpRecommendationsUrl = (section) => {
    const baseUrl = section.dataset.url;
    const productId = section.dataset.productId;
    if (!baseUrl || !productId) return null;

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('section_id', 'kairaus-pdp-recommendations');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', section.dataset.limit || '8');
    url.searchParams.set('intent', 'related');

    if (section.dataset.recShowBadge != null) {
      url.searchParams.set('rec_show_badge', section.dataset.recShowBadge);
    }
    if (section.dataset.recEnableImageSlider != null) {
      url.searchParams.set('rec_enable_image_slider', section.dataset.recEnableImageSlider);
    }
    if (section.dataset.recSwatchLimit != null) {
      url.searchParams.set('rec_swatch_limit', section.dataset.recSwatchLimit);
    }
    if (section.dataset.recCartBtnLabel) {
      url.searchParams.set('rec_cart_btn_label', section.dataset.recCartBtnLabel);
    }
    if (section.dataset.recFallbackHandles) {
      url.searchParams.set('rec_fallback_handles', section.dataset.recFallbackHandles);
    }

    return url.toString();
  };

  const RECENTLY_VIEWED_STORAGE_KEY = 'kairaus:recently-viewed';

  const trackRecentlyViewedProduct = (section) => {
    const handle = section.dataset.productHandle;
    const productId = section.dataset.productId;
    if (!handle || !productId) return;

    let items = [];
    try {
      items = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) || '[]');
      if (!Array.isArray(items)) items = [];
    } catch {
      items = [];
    }

    items = items.filter((item) => item && item.handle !== handle);
    items.unshift({ handle, id: productId });
    items = items.slice(0, 12);
    localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(items));
  };

  const getRecentlyViewedHandles = (section) => {
    const currentHandle = section.dataset.productHandle;
    let items = [];
    try {
      items = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) || '[]');
      if (!Array.isArray(items)) items = [];
    } catch {
      items = [];
    }

    return items
      .map((item) => item?.handle)
      .filter((handle) => handle && handle !== currentHandle);
  };

  const buildRecentlyViewedUrl = (section) => {
    const handles = getRecentlyViewedHandles(section);
    const fallbackCollection = section.dataset.rvFallbackCollection;

    if (!handles.length && !fallbackCollection) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'kairaus-pdp-recently-viewed');
    if (handles.length) {
      url.searchParams.set('handles', handles.join(','));
    }
    if (fallbackCollection) {
      url.searchParams.set('rv_collection', fallbackCollection);
    }
    url.searchParams.set('exclude_product_id', section.dataset.productId || '');
    url.searchParams.set('limit', section.dataset.limit || '8');
    url.searchParams.set('rv_limit', section.dataset.limit || '8');

    if (section.dataset.rvShowBadge != null) {
      url.searchParams.set('rv_show_badge', section.dataset.rvShowBadge);
    }
    if (section.dataset.rvEnableImageSlider != null) {
      url.searchParams.set('rv_enable_image_slider', section.dataset.rvEnableImageSlider);
    }
    if (section.dataset.rvSwatchLimit != null) {
      url.searchParams.set('rv_swatch_limit', section.dataset.rvSwatchLimit);
    }
    if (section.dataset.rvCartBtnLabel) {
      url.searchParams.set('rv_cart_btn_label', section.dataset.rvCartBtnLabel);
    }

    return url.toString();
  };

  const finalizeRecentlyViewedSection = (section, grid) => {
    section.removeAttribute('aria-busy');

    if (!grid.children.length) {
      section.hidden = true;
      return;
    }

    section.hidden = false;
    grid.setAttribute('data-kairaus-recently-viewed', '');
    grid.setAttribute('data-kpt-product-grid-root', '');
    dispatchGridInit(section);
  };

  const loadKairausRecentlyViewed = async () => {
    const sections = document.querySelectorAll('[data-kairaus-recently-viewed-section]');

    for (const section of sections) {
      trackRecentlyViewedProduct(section);

      const grid = section.querySelector('.rv-prd-grid');
      if (!grid) continue;

      const handles = getRecentlyViewedHandles(section);
      const hasServerRenderedCards = grid.children.length > 0;

      if (!handles.length) {
        finalizeRecentlyViewedSection(section, grid);
        continue;
      }

      const url = buildRecentlyViewedUrl(section);
      if (!url) {
        finalizeRecentlyViewedSection(section, grid);
        continue;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.error('Recently viewed fetch failed', response.status, url);
          if (hasServerRenderedCards) {
            finalizeRecentlyViewedSection(section, grid);
          } else {
            section.hidden = true;
            section.removeAttribute('aria-busy');
          }
          continue;
        }

        const html = await response.text();
        const container = document.createElement('div');
        container.innerHTML = html;

        const fetchedGrid =
          container.querySelector('[data-kairaus-recently-viewed]') ||
          container.querySelector('#shopify-section-kairaus-pdp-recently-viewed .rv-prd-grid') ||
          container.querySelector('.shopify-section .rv-prd-grid') ||
          container.querySelector('.rv-prd-grid');

        if (!fetchedGrid?.innerHTML?.trim()) {
          finalizeRecentlyViewedSection(section, grid);
          continue;
        }

        grid.innerHTML = fetchedGrid.innerHTML;

        if (fetchedGrid.getAttribute('data-kpt-currency')) {
          grid.setAttribute('data-kpt-currency', fetchedGrid.getAttribute('data-kpt-currency'));
        }

        finalizeRecentlyViewedSection(section, grid);
      } catch (error) {
        console.error('Recently viewed fetch failed', error);
        if (hasServerRenderedCards) {
          finalizeRecentlyViewedSection(section, grid);
        } else {
          section.hidden = true;
          section.removeAttribute('aria-busy');
        }
      }
    }
  };

  const loadKairausPdpRecommendations = async () => {
    const sections = document.querySelectorAll('[data-kairaus-pdp-recommendations]');

    for (const section of sections) {
      const url = buildPdpRecommendationsUrl(section);
      if (!url) continue;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.error('PDP recommendations fetch failed', response.status, url);
          section.removeAttribute('aria-busy');
          continue;
        }

        const html = await response.text();
        const container = document.createElement('div');
        container.innerHTML = html;

        const track = section.querySelector('.suggested-products-wrap');
        const fetchedTrack =
          container.querySelector('[data-kairaus-pdp-recommendations] .suggested-products-wrap') ||
          container.querySelector('.suggested-products-wrap');

        if (!track || !fetchedTrack) {
          console.error('PDP recommendations: track not found in response', url);
          section.removeAttribute('aria-busy');
          continue;
        }

        track.innerHTML = fetchedTrack.innerHTML;
        section.removeAttribute('aria-busy');

        if (!track.children.length) {
          section.hidden = true;
          continue;
        }

        section.hidden = false;
        section.dataset.pdpSliderInit = '';
        initRecommendationsSection(section);
      } catch (error) {
        console.error('PDP recommendations fetch failed', error);
        section.removeAttribute('aria-busy');
      }
    }
  };

  const PDP_RATINGS_MOBILE_MQ = window.matchMedia('(max-width: 991px)');

  /**
   * @param {Element | null} el
   */
  const isPdpBlockVisible = (el) => {
    if (!(el instanceof HTMLElement) || !el.isConnected) {
      return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    return el.getClientRects().length > 0;
  };

  /**
   * @type {WeakMap<HTMLElement, AbortController>}
   */
  const pdCustomSliderControllers = new WeakMap();

  /**
   * @param {HTMLElement | null | undefined} sliderRoot
   */
  const destroyPdCustomSlider = (sliderRoot) => {
    if (!(sliderRoot instanceof HTMLElement)) {
      return;
    }
    pdCustomSliderControllers.get(sliderRoot)?.abort();
    pdCustomSliderControllers.delete(sliderRoot);
    delete sliderRoot.dataset.pdSliderInit;

    const track = sliderRoot.querySelector('[data-pd-slider-track]');
    if (track instanceof HTMLElement) {
      track.style.transform = '';
    }
  };

  /**
   * @param {HTMLElement} sliderRoot
   */
  const initPdCustomSlider = (sliderRoot) => {
    if (!(sliderRoot instanceof HTMLElement) || !isPdpBlockVisible(sliderRoot)) {
      return;
    }

    destroyPdCustomSlider(sliderRoot);

    const track = sliderRoot.querySelector('[data-pd-slider-track]');
    const slides = [...sliderRoot.querySelectorAll('[data-pd-slider-slide]')];
    const dots = [...sliderRoot.querySelectorAll('[data-pd-slider-dots] .pd-dot')];

    if (!(track instanceof HTMLElement) || slides.length === 0) {
      return;
    }

    let index = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchLock = null;

    const controller = new AbortController();
    const { signal } = controller;
    pdCustomSliderControllers.set(sliderRoot, controller);

    const updateDots = () => {
      for (const [dotIndex, dot] of dots.entries()) {
        const isActive = dotIndex === index;
        dot.classList.toggle('pd-dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    };

    const goTo = (nextIndex) => {
      if (slides.length <= 1) {
        index = 0;
        track.style.transform = '';
        updateDots();
        return;
      }

      index = Math.max(0, Math.min(slides.length - 1, nextIndex));
      track.style.transform = `translateX(-${index * 100}%)`;
      updateDots();
    };

    for (const [dotIndex, dot] of dots.entries()) {
      dot.addEventListener(
        'click',
        () => {
          goTo(dotIndex);
        },
        { signal }
      );
    }

    if (slides.length > 1) {
      track.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) {
            return;
          }
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
          touchLock = null;
        },
        { passive: true, signal }
      );

      track.addEventListener(
        'touchmove',
        (event) => {
          if (event.touches.length !== 1 || touchLock === 'y') {
            return;
          }

          const deltaX = event.touches[0].clientX - touchStartX;
          const deltaY = event.touches[0].clientY - touchStartY;

          if (touchLock === null && (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)) {
            touchLock = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';
          }
        },
        { passive: true, signal }
      );

      track.addEventListener(
        'touchend',
        (event) => {
          if (touchLock !== 'x') {
            return;
          }

          const deltaX = event.changedTouches[0].clientX - touchStartX;
          if (Math.abs(deltaX) < 40) {
            return;
          }

          if (deltaX < 0) {
            goTo(index + 1);
          } else {
            goTo(index - 1);
          }
        },
        { passive: true, signal }
      );
    }

    goTo(0);
    sliderRoot.dataset.pdSliderInit = '1';
  };

  /**
   * @param {HTMLElement} sidebar
   */
  const setupPdpRatingsSwipers = (sidebar) => {
    if (!(sidebar instanceof HTMLElement)) {
      return;
    }

    const isMobile = PDP_RATINGS_MOBILE_MQ.matches;
    const desktopWrap = sidebar.querySelector('.pd-quote');
    const mobileWrap = sidebar.querySelector('.pd-review-mobile');
    const desktopSlider = sidebar.querySelector('[data-pd-quote-slider]');
    const mobileSlider = sidebar.querySelector('[data-pd-review-mobile-slider]');

    destroyPdCustomSlider(desktopSlider instanceof HTMLElement ? desktopSlider : null);
    destroyPdCustomSlider(mobileSlider instanceof HTMLElement ? mobileSlider : null);

    if (isMobile && mobileSlider instanceof HTMLElement && isPdpBlockVisible(mobileWrap)) {
      initPdCustomSlider(mobileSlider);
      return;
    }

    if (!isMobile && desktopSlider instanceof HTMLElement && isPdpBlockVisible(desktopWrap)) {
      initPdCustomSlider(desktopSlider);
    }
  };

  const refreshAllPdpRatingsSwipers = () => {
    document.querySelectorAll('[data-pdp-ratings-sidebar]').forEach(setupPdpRatingsSwipers);
  };

  /** @type {IntersectionObserver | null} */
  let pdpRatingsObserver = null;

  const observePdpRatingsSidebars = () => {
    if (pdpRatingsObserver) {
      pdpRatingsObserver.disconnect();
    }

    pdpRatingsObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) {
            continue;
          }
          setupPdpRatingsSwipers(entry.target);
        }
      },
      { rootMargin: '80px 0px', threshold: 0.01 }
    );

    document.querySelectorAll('[data-pdp-ratings-sidebar]').forEach((sidebar) => {
      pdpRatingsObserver.observe(sidebar);
      setupPdpRatingsSwipers(sidebar);
    });
  };

  let resizeRefreshTimer = 0;
  const schedulePdpRatingsRefresh = () => {
    window.clearTimeout(resizeRefreshTimer);
    resizeRefreshTimer = window.setTimeout(refreshAllPdpRatingsSwipers, 150);
  };

  PDP_RATINGS_MOBILE_MQ.addEventListener('change', schedulePdpRatingsRefresh);
  window.addEventListener('orientationchange', schedulePdpRatingsRefresh);
  window.addEventListener('resize', schedulePdpRatingsRefresh);
  window.addEventListener('pageshow', refreshAllPdpRatingsSwipers);

  /**
   * @param {number} cents
   * @param {string} [currency]
   */
  const formatMoneyFromCents = (cents, currency) => {
    const n = Number(cents);
    if (!Number.isFinite(n)) {
      return '';
    }
    const c = currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: c,
      }).format(n / 100);
    } catch {
      return `₹${(n / 100).toFixed(2)}`;
    }
  };

  /**
   * @typedef {Object} FbtItem
   * @property {number} variantId
   * @property {number} price
   * @property {boolean} available
   */

  /**
   * @typedef {Object} FbtConfig
   * @property {number} discountPercent
   * @property {string} currency
   * @property {string} addButtonLabel
   * @property {FbtItem[]} items
   */

  const FBT_LAYOUT_MQ = window.matchMedia('(max-width: 991px)');

  /**
   * @param {Element | null} el
   */
  const isFbtLayoutVisible = (el) => {
    if (!(el instanceof HTMLElement) || !el.isConnected) {
      return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  const getActiveFbtLayout = (root) => {
    const mobile = root.querySelector('.cd-mobile');
    const desktop = root.querySelector('.combine-deal');

    if (mobile && isFbtLayoutVisible(mobile)) {
      return mobile;
    }
    if (desktop && isFbtLayoutVisible(desktop)) {
      return desktop;
    }
    return null;
  };

  /**
   * @param {HTMLElement} container
   */
  const destroyFbtSwipersIn = (container) => {
    container.querySelectorAll('[data-kpt-image-swiper]').forEach((slider) => {
      if (slider instanceof HTMLElement && slider.swiper) {
        slider.swiper.destroy(true, true);
      }
    });
  };

  /**
   * @param {HTMLElement} root
   */
  const initFbtImageSwipers = (root) => {
    if (!(root instanceof HTMLElement)) {
      return;
    }

    whenSwiperReady(() => {
      const activeLayout = getActiveFbtLayout(root);
      const mobile = root.querySelector('.cd-mobile');
      const desktop = root.querySelector('.combine-deal');

      if (mobile instanceof HTMLElement && mobile !== activeLayout) {
        destroyFbtSwipersIn(mobile);
      }
      if (desktop instanceof HTMLElement && desktop !== activeLayout) {
        destroyFbtSwipersIn(desktop);
      }

      if (!activeLayout) {
        return;
      }

      const sliders = activeLayout.querySelectorAll('[data-kpt-image-swiper]');
      for (const slider of sliders) {
        if (!(slider instanceof HTMLElement)) {
          continue;
        }

        const paginationEl =
          slider.querySelector('[data-kpt-image-pagination]') ||
          slider.querySelector('.swiper-pagination');
        const slideEls = slider.querySelectorAll('.swiper-slide');
        if (slideEls.length < 2) {
          continue;
        }

        if (slider.swiper) {
          slider.swiper.update();
          continue;
        }

        /** @type {import('swiper').SwiperOptions} */
        const swiperOpts = {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: slideEls.length >= 3,
          speed: 300,
          grabCursor: true,
          watchOverflow: true,
          nested: true,
          observer: true,
          observeParents: true,
          resizeObserver: true,
        };

        if (paginationEl instanceof HTMLElement) {
          swiperOpts.pagination = { el: paginationEl, clickable: true };
        }

        const instance = new window.Swiper(slider, swiperOpts);
        requestAnimationFrame(() => {
          instance.update();
        });
      }
    });
  };

  const refreshAllFbtImageSwipers = () => {
    document.querySelectorAll('[data-pdp-fbt]').forEach((root) => {
      if (root instanceof HTMLElement) {
        initFbtImageSwipers(root);
      }
    });
  };

  let fbtResizeTimer = 0;
  const scheduleFbtSwiperRefresh = () => {
    window.clearTimeout(fbtResizeTimer);
    fbtResizeTimer = window.setTimeout(refreshAllFbtImageSwipers, 150);
  };

  FBT_LAYOUT_MQ.addEventListener('change', scheduleFbtSwiperRefresh);
  window.addEventListener('orientationchange', scheduleFbtSwiperRefresh);
  window.addEventListener('resize', scheduleFbtSwiperRefresh);

  /**
   * @param {HTMLElement} root
   */
  const initPdpFbt = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.pdpFbtInit === '1') {
      return;
    }

    const configEl = root.querySelector('[data-fbt-config]');
    if (!configEl?.textContent?.trim()) {
      return;
    }

    /** @type {FbtConfig | null} */
    let config = null;
    try {
      config = JSON.parse(configEl.textContent);
    } catch {
      return;
    }

    if (!config?.items?.length) {
      return;
    }

    root.dataset.pdpFbtInit = '1';

    const currency = config.currency || 'USD';
    const discountPercent = Math.max(0, Number(config.discountPercent) || 0);
    const addButtons = root.querySelectorAll('[data-fbt-add-all]');
    const subtotalEl = root.querySelector('[data-fbt-subtotal]');
    const discountEl = root.querySelector('[data-fbt-discount]');
    const totalEl = root.querySelector('[data-fbt-total]');
    const mobileTotalEl = root.querySelector('[data-fbt-mobile-total]');

    /**
     * @param {number} index
     */
    const isIndexSelected = (index) => {
      const boxes = root.querySelectorAll(`[data-fbt-checkbox][data-fbt-index="${index}"]`);
      for (const box of boxes) {
        if (box instanceof HTMLInputElement && box.checked && !box.disabled) {
          return true;
        }
      }
      return false;
    };

    const getSelectedItems = () => {
      return config.items.filter((item, index) => {
        if (!item.available) {
          return false;
        }
        return isIndexSelected(index);
      });
    };

    const updateTotals = () => {
      const selected = getSelectedItems();
      const subtotalCents = selected.reduce((sum, item) => sum + Number(item.price), 0);
      const discountCents = Math.round(subtotalCents * (discountPercent / 100));
      const totalCents = Math.max(0, subtotalCents - discountCents);
      const count = selected.length;

      if (subtotalEl) {
        subtotalEl.textContent = formatMoneyFromCents(subtotalCents, currency);
      }
      if (discountEl) {
        discountEl.textContent = formatMoneyFromCents(discountCents, currency);
      }
      if (totalEl) {
        totalEl.textContent = formatMoneyFromCents(totalCents, currency);
      }
      if (mobileTotalEl) {
        mobileTotalEl.textContent = formatMoneyFromCents(totalCents, currency);
      }

      const labelBase = config.addButtonLabel || 'Add All [COUNT] To Cart';
      const buttonText = labelBase.replace('[COUNT]', String(Math.max(count, 0)));

      addButtons.forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) {
          return;
        }
        btn.textContent = buttonText;
        btn.disabled = count === 0;
      });
    };

    root.querySelectorAll('[data-fbt-checkbox]').forEach((checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) {
        return;
      }

      checkbox.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      checkbox.addEventListener('change', () => {
        const index = checkbox.getAttribute('data-fbt-index');
        if (!index) {
          return;
        }
        root
          .querySelectorAll(`[data-fbt-checkbox][data-fbt-index="${index}"]`)
          .forEach((peer) => {
            if (peer instanceof HTMLInputElement && peer !== checkbox) {
              peer.checked = checkbox.checked;
            }
          });
        updateTotals();
      });
    });

    addButtons.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) {
        return;
      }

      btn.addEventListener('click', async () => {
        const selected = getSelectedItems();
        if (!selected.length) {
          return;
        }

        const cartUrl = window.theme?.routes?.cartAdd;
        if (!cartUrl) {
          return;
        }

        btn.disabled = true;
        btn.classList.add('is-loading');

        try {
          const response = await fetch(cartUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: selected.map((item) => ({
                id: item.variantId,
                quantity: 1,
              })),
            }),
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            const message =
              data?.description || data?.message || 'Unable to add products to cart';
            throw new Error(typeof message === 'string' ? message : 'Unable to add products to cart');
          }

          document.dispatchEvent(
            new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true })
          );

          if (window.theme?.settings?.afterAddToCart === 'drawer') {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
          }

          root.dispatchEvent(
            new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: { items: selected.map((item) => item.variantId) },
            })
          );
        } catch (error) {
          console.error(error);
          root.dispatchEvent(
            new CustomEvent('on:cart:error', {
              bubbles: true,
              detail: {
                error: error instanceof Error ? error.message : 'Cart error',
              },
            })
          );
        } finally {
          btn.classList.remove('is-loading');
          updateTotals();
        }
      });
    });

    initFbtImageSwipers(root);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => initFbtImageSwipers(root));
    });
    updateTotals();
  };

  const boot = () => {
    document.querySelectorAll('[data-kairaus-pdp-recommendations]').forEach(initRecommendationsSection);
    document.querySelectorAll('[data-pdp-fbt]').forEach(initPdpFbt);
    refreshAllFbtImageSwipers();
    observePdpRatingsSidebars();
    loadKairausPdpRecommendations();
    loadKairausRecentlyViewed();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', () => {
    refreshAllPdpRatingsSwipers();
    refreshAllFbtImageSwipers();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const section =
      target.matches('[data-kairaus-pdp-recommendations]')
        ? target
        : target.querySelector('[data-kairaus-pdp-recommendations]');

    if (section) loadKairausPdpRecommendations();

    const rvSection =
      target.matches('[data-kairaus-recently-viewed-section]')
        ? target
        : target.querySelector('[data-kairaus-recently-viewed-section]');

    if (rvSection) loadKairausRecentlyViewed();

    const fbt = target.querySelector('[data-pdp-fbt]');
    if (fbt instanceof HTMLElement) {
      delete fbt.dataset.pdpFbtInit;
      initPdpFbt(fbt);
      initFbtImageSwipers(fbt);
    }

    observePdpRatingsSidebars();
  });
})();
